/**
 * Discovery-signals model — AI-Ready benchmark aggregation.
 *
 * AI-Ready aggregates the published agent-readiness benchmark (6 dimensions,
 * 21 checks) into four agent journey stages — discover, learn, operate,
 * verify — because that is the order an agent actually fails in. Scores
 * render only from real published assessments; there is no preview/mock
 * fallback, so an unaudited tool shows no benchmark card at all.
 *
 * GEO stays methodology-only (see /benchmarks) until a real audit connects.
 */

import type { PublicAgentReadiness } from '@/modules/agent-readiness/queries';
import { AGENT_READINESS_CHECK_KEYS, type AgentReadinessCheckKey } from '@/modules/agent-readiness/benchmark';

export type ScoreBand = 'good' | 'fair' | 'poor';

/** ≥80 green, 60-79 amber, <60 rose. */
export function scoreBand(score: number): ScoreBand {
  if (score >= 80) return 'good';
  if (score >= 60) return 'fair';
  return 'poor';
}

/** Letter grade for the GEO card: A ≥90, B ≥80, C ≥70, D ≥60, else E. */
export function scoreGrade(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'E';
}

export type AiReadyStage = 'discover' | 'learn' | 'operate' | 'verify';

export const AI_READY_STAGES: AiReadyStage[] = ['discover', 'learn', 'operate', 'verify'];

/** Benchmark checks mapped to the stage they serve. */
const STAGE_CHECKS: Record<AiReadyStage, AgentReadinessCheckKey[]> = {
  discover: ['llms_txt', 'sitemap'],
  learn: ['docs', 'quickstart', 'api_reference', 'openapi', 'request_examples', 'response_examples', 'error_documentation'],
  operate: ['cli', 'cli_non_interactive', 'sdk', 'mcp', 'webhooks', 'authentication', 'structured_import_export'],
  verify: ['cli_structured_output', 'success_verification', 'rate_limits', 'version_information', 'changelog'],
};

export type AiReadySample = {
  score: number;
  /** A benchmark version string identifying the real assessment run. */
  source: string;
  stages: Array<{ stage: AiReadyStage; passed: number; total: number }>;
};

/**
 * Aggregate a published agent-readiness assessment into the four stages.
 * `passed` counts verified (+partial as half-weight via rounding at the score
 * level); score per stage = round(passed-weighted ratio × 100).
 *
 * Returns null when no assessment exists or it lacks an overall score — the
 * caller must not render a benchmark card in that case.
 */
export function aiReadyFromAssessment(
  assessment: PublicAgentReadiness | null,
): AiReadySample | null {
  if (!assessment || assessment.overallScore == null) return null;
  const statusByKey = new Map(assessment.checks.map((check) => [check.key, check.status]));
  const stages = AI_READY_STAGES.map((stage) => {
    const keys = STAGE_CHECKS[stage].filter((key) => AGENT_READINESS_CHECK_KEYS.includes(key));
    let weight = 0;
    for (const key of keys) {
      const status = statusByKey.get(key);
      if (status === 'verified') weight += 1;
      else if (status === 'partial') weight += 0.5;
    }
    return {
      stage,
      passed: Math.round(weight),
      total: keys.length,
    };
  });
  return {
    score: assessment.overallScore,
    source: assessment.benchmarkVersion,
    stages,
  };
}

// ─── GEO methodology (documentation only until a real audit connects) ────────

export type GeoDimension =
  | 'crawler_access'
  | 'machine_readability'
  | 'structured_data'
  | 'citability'
  | 'trust_eeat';

export const GEO_DIMENSIONS: GeoDimension[] = [
  'crawler_access',
  'machine_readability',
  'structured_data',
  'citability',
  'trust_eeat',
];
